import { List, ListItem } from '@jsluna/list';
import { Card } from '@jsluna/card';
import { Heading } from '@jsluna/typography';
import { Search } from '@jsluna/icons';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { filterProps, getComponentName } from '@jsluna/utils';
import React, { Component, useState, Fragment } from 'react';
import { Button, SearchButton, CancelButton } from '@jsluna/button';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

var Input = function Input(_ref) {
  var id = _ref.id,
      name = _ref.name,
      type = _ref.type,
      placeholder = _ref.placeholder,
      disabled = _ref.disabled,
      onBlur = _ref.onBlur,
      onChange = _ref.onChange,
      onFocus = _ref.onFocus,
      value = _ref.value,
      defaultValue = _ref.defaultValue,
      checked = _ref.checked,
      defaultChecked = _ref.defaultChecked,
      minLength = _ref.minLength,
      maxLength = _ref.maxLength,
      min = _ref.min,
      max = _ref.max,
      step = _ref.step,
      autoComplete = _ref.autoComplete,
      autoCapitalize = _ref.autoCapitalize,
      required = _ref.required,
      inputMode = _ref.inputMode,
      pattern = _ref.pattern,
      readOnly = _ref.readOnly,
      spellCheck = _ref.spellCheck,
      tabIndex = _ref.tabIndex,
      className = _ref.className,
      invalid = _ref.invalid,
      innerRef = _ref.innerRef,
      rest = _objectWithoutPropertiesLoose(_ref, ["id", "name", "type", "placeholder", "disabled", "onBlur", "onChange", "onFocus", "value", "defaultValue", "checked", "defaultChecked", "minLength", "maxLength", "min", "max", "step", "autoComplete", "autoCapitalize", "required", "inputMode", "pattern", "readOnly", "spellCheck", "tabIndex", "className", "invalid", "innerRef"]);

  return /*#__PURE__*/React.createElement("input", _extends({}, rest, {
    id: id || name,
    name: name,
    type: type,
    className: className,
    onBlur: onBlur,
    onChange: onChange,
    onFocus: onFocus,
    value: value,
    defaultValue: defaultValue,
    checked: checked,
    defaultChecked: defaultChecked,
    placeholder: placeholder,
    tabIndex: tabIndex,
    autoComplete: autoComplete,
    autoCapitalize: autoCapitalize,
    inputMode: inputMode,
    spellCheck: spellCheck,
    required: required,
    minLength: minLength,
    maxLength: maxLength,
    min: min,
    max: max,
    step: step,
    pattern: pattern,
    readOnly: readOnly,
    disabled: disabled,
    "aria-disabled": disabled || undefined,
    "aria-invalid": required ? invalid : invalid === true || undefined,
    ref: innerRef
  }));
};

Input.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  autoComplete: PropTypes.string,
  autoCapitalize: PropTypes.string,
  required: PropTypes.bool,
  inputMode: PropTypes.string,
  pattern: PropTypes.oneOfType([PropTypes.instanceOf(RegExp), PropTypes.string]),
  readOnly: PropTypes.bool,
  spellCheck: PropTypes.oneOf(['default', true, false]),
  tabIndex: PropTypes.number,
  className: PropTypes.string,
  invalid: PropTypes.bool,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
};
Input.defaultProps = {
  id: undefined,
  type: 'text',
  disabled: false,
  placeholder: undefined,
  onBlur: undefined,
  onChange: undefined,
  onFocus: undefined,
  value: undefined,
  defaultValue: undefined,
  checked: undefined,
  defaultChecked: undefined,
  minLength: undefined,
  maxLength: undefined,
  min: undefined,
  max: undefined,
  step: undefined,
  autoComplete: undefined,
  autoCapitalize: undefined,
  required: undefined,
  inputMode: undefined,
  pattern: undefined,
  readOnly: false,
  spellCheck: undefined,
  tabIndex: undefined,
  className: undefined,
  invalid: false,
  innerRef: undefined
};
Input.displayName = 'Input';
var baseClassName = 'ln-c-form-option';

var FormOption = /*#__PURE__*/function (_Component) {
  _inheritsLoose(FormOption, _Component);

  function FormOption(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.inputRef = null;
    _this.clickHander = _this.clickHander.bind(_assertThisInitialized(_this));
    return _this;
  }

  var _proto = FormOption.prototype;

  _proto.clickHander = function clickHander(e) {
    var _this$props = this.props,
        focusOnClick = _this$props.focusOnClick,
        onClick = _this$props.onClick;

    if (focusOnClick && this.inputRef) {
      this.inputRef.focus();
    }

    if (onClick) {
      onClick(e);
    }
  };

  _proto.render = function render() {
    var _this2 = this;

    var _this$props2 = this.props,
        element = _this$props2.element,
        label = _this$props2.label,
        hideLabel = _this$props2.hideLabel,
        className = _this$props2.className,
        fullWidth = _this$props2.fullWidth,
        inline = _this$props2.inline,
        type = _this$props2.type,
        outlined = _this$props2.outlined,
        name = _this$props2.name,
        focusOnClick = _this$props2.focusOnClick,
        rest = _objectWithoutPropertiesLoose(_this$props2, ["element", "label", "hideLabel", "className", "fullWidth", "inline", "type", "outlined", "name", "focusOnClick"]);

    var Element = element; // eslint-disable-next-line react/destructuring-assignment

    var id = this.props.value ? name + "-" + this.props.value : name;
    return /*#__PURE__*/React.createElement(Element, {
      className: classnames(baseClassName, baseClassName + "--" + type, fullWidth && baseClassName + "--full", inline && baseClassName + "--inline", hideLabel && baseClassName + "--hide-label", outlined && baseClassName + "--outlined", className)
    }, /*#__PURE__*/React.createElement(Input, _extends({}, filterProps(rest, ['focusOnClick', 'onClick']), {
      name: name,
      type: type === 'switch' ? 'checkbox' : type,
      className: baseClassName + "__input",
      id: id,
      innerRef: function innerRef(el) {
        _this2.inputRef = el;
      },
      onClick: this.clickHander
    })), /*#__PURE__*/React.createElement("label", {
      className: baseClassName + "__label",
      htmlFor: id
    }, /*#__PURE__*/React.createElement("span", null, label)));
  };

  return FormOption;
}(Component);

FormOption.propTypes = {
  /** Allows the top-level element to be customized */
  element: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),

  /** The name that gets submitted to the server */
  name: PropTypes.string.isRequired,

  /** What format the option should take */
  type: PropTypes.oneOf(['checkbox', 'radio', 'switch']),

  /** Text describing the input */
  label: PropTypes.node,

  /** Makes the label only visible to screen readers */
  hideLabel: PropTypes.bool,
  className: PropTypes.string,

  /** Change to block level display */
  fullWidth: PropTypes.bool,

  /** Allows option to be used as an inline element */
  inline: PropTypes.bool,

  /** Value of the input, also used to provide a unique id */
  value: PropTypes.string,

  /** Whether the option has a border */
  outlined: PropTypes.bool,

  /** Apply focus when option is clicked, to give consistent focus behaviour on MacOS */
  focusOnClick: PropTypes.bool,
  onClick: PropTypes.func
};
FormOption.defaultProps = {
  element: 'div',
  type: 'radio',
  label: undefined,
  hideLabel: false,
  className: undefined,
  fullWidth: false,
  inline: false,
  value: undefined,
  outlined: false,
  focusOnClick: false,
  onClick: undefined
};
FormOption.displayName = 'FormOption';

var Checkbox = function Checkbox(props) {
  return /*#__PURE__*/React.createElement(FormOption, _extends({
    type: "checkbox"
  }, props));
};

Checkbox.displayName = 'Checkbox';

var LabelInfo = function LabelInfo(_ref) {
  var className = _ref.className,
      children = _ref.children,
      rest = _objectWithoutPropertiesLoose(_ref, ["className", "children"]);

  return /*#__PURE__*/React.createElement("span", _extends({}, rest, {
    className: classnames('ln-c-label__info', className)
  }), children);
};

LabelInfo.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string
};
LabelInfo.defaultProps = {
  className: undefined
};
LabelInfo.displayName = 'LabelInfo';
var DEFAULT_REQUIRED_INDICATOR = '';
var DEFAULT_OPTIONAL_INDICATOR = '(optional)';

var Label = function Label(_ref) {
  var element = _ref.element,
      htmlFor = _ref.htmlFor,
      requiredLabel = _ref.requiredLabel,
      optionalLabel = _ref.optionalLabel,
      children = _ref.children,
      className = _ref.className,
      hidden = _ref.hidden,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "htmlFor", "requiredLabel", "optionalLabel", "children", "className", "hidden"]);

  var Element = element;
  var indicator;

  if (optionalLabel !== undefined) {
    indicator = optionalLabel === true ? DEFAULT_OPTIONAL_INDICATOR : optionalLabel;
  }

  if (requiredLabel !== undefined) {
    indicator = requiredLabel === true ? DEFAULT_REQUIRED_INDICATOR : requiredLabel;
  }

  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    htmlFor: htmlFor,
    className: classnames(className, 'ln-c-label', hidden && 'ln-u-visually-hidden')
  }), children, indicator && /*#__PURE__*/React.createElement(LabelInfo, null, " ", indicator));
};

Label.propTypes = {
  element: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),
  htmlFor: PropTypes.string.isRequired,
  requiredLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  optionalLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  hidden: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string
};
Label.defaultProps = {
  element: 'label',
  requiredLabel: undefined,
  optionalLabel: undefined,
  hidden: false,
  className: undefined
};
Label.displayName = 'Label';

var FieldInfo = function FieldInfo(_ref) {
  var element = _ref.element,
      id = _ref.id,
      children = _ref.children,
      error = _ref.error,
      warning = _ref.warning,
      className = _ref.className,
      displayFirst = _ref.displayFirst,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "id", "children", "error", "warning", "className", "displayFirst"]);

  var Element = element;
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    id: id,
    className: classnames(className, 'ln-c-field-info', {
      'ln-c-field-info--validation-first': displayFirst,
      'ln-c-field-info--error': error || warning,
      'ln-c-field-info--extra': !error && !warning
    }),
    role: error || warning ? 'alert' : undefined,
    "aria-live": error ? 'assertive' : undefined
  }), children);
};

FieldInfo.propTypes = {
  element: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),
  id: PropTypes.string,
  children: PropTypes.node.isRequired,
  error: PropTypes.bool,
  warning: PropTypes.bool,
  className: PropTypes.string,
  displayFirst: PropTypes.bool
};
FieldInfo.defaultProps = {
  element: 'div',
  id: undefined,
  error: false,
  warning: false,
  className: undefined,
  displayFirst: false
};
FieldInfo.displayName = 'FieldInfo';

var getRequiredFlag = function getRequiredFlag(required, optional) {
  return required !== undefined || optional !== undefined ? !!required : undefined;
};

var getDescribedByIds = function getDescribedByIds(_ref) {
  var name = _ref.name,
      info = _ref.info,
      error = _ref.error,
      warning = _ref.warning;
  return [info !== undefined && name + "Info", (error !== undefined || warning !== undefined) && name + "Validation"].filter(function (id) {
    return !!id;
  });
};

var getAriaDescribedBy = function getAriaDescribedBy(props) {
  var describedBy = getDescribedByIds(props);
  return describedBy.length > 0 ? describedBy.join(' ') : undefined;
};
/**
  `FormGroup` can be used to create labelled, accessible form fields.
  It is used internally by various `Fields` within Luna e.g. `TextInputField`,
  but can also be used to create your own custom form elements.
*/


var FormGroup = function FormGroup(_ref) {
  var element = _ref.element,
      labelElement = _ref.labelElement,
      name = _ref.name,
      required = _ref.required,
      optional = _ref.optional,
      label = _ref.label,
      error = _ref.error,
      warning = _ref.warning,
      info = _ref.info,
      children = _ref.children,
      className = _ref.className,
      validationFirst = _ref.validationFirst,
      hideLabel = _ref.hideLabel,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "labelElement", "name", "required", "optional", "label", "error", "warning", "info", "children", "className", "validationFirst", "hideLabel"]);

  var Element = element;
  var usedFieldset = element === 'fieldset';
  var hasError = !!error;
  var hasWarning = !!warning;
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    className: classnames(className, 'ln-c-form-group', {
      'has-error': hasError,
      'has-warning': hasWarning
    })
  }), !!label && /*#__PURE__*/React.createElement(Label, {
    element: labelElement,
    htmlFor: name,
    requiredLabel: required,
    optionalLabel: optional,
    hidden: hideLabel
  }, label), !!info && /*#__PURE__*/React.createElement(FieldInfo, {
    id: name + "Info"
  }, info), !usedFieldset && (hasError || hasWarning) && /*#__PURE__*/React.createElement(FieldInfo, {
    id: name + "Validation",
    displayFirst: validationFirst,
    error: hasError,
    warning: hasWarning
  }, error || warning), typeof children === 'function' ? children({
    name: name,
    required: getRequiredFlag(required, optional),
    'aria-describedby': getAriaDescribedBy({
      name: name,
      info: info,
      warning: warning,
      error: error
    }),
    invalid: error !== undefined ? !!error : undefined
  }) : children, usedFieldset && (hasError || hasWarning) && /*#__PURE__*/React.createElement(FieldInfo, {
    id: name + "Validation",
    displayFirst: validationFirst,
    error: hasError,
    warning: hasWarning
  }, error || warning));
};

FormGroup.propTypes = {
  element: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),
  labelElement: Label.propTypes.element,
  name: PropTypes.string.isRequired,
  required: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  optional: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  label: PropTypes.node,
  hideLabel: PropTypes.bool,
  error: PropTypes.node,
  warning: PropTypes.node,
  info: PropTypes.node,

  /**
   If a function is passed it will be used as a "render prop"
   with `name`, `required`, `invalid` and `aria-describedby` passed
   down on render. This can then be spread onto the underlying input.
  */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  className: PropTypes.string,
  validationFirst: PropTypes.bool
};
FormGroup.defaultProps = {
  element: 'div',
  labelElement: undefined,
  required: undefined,
  optional: undefined,
  label: undefined,
  hideLabel: false,
  info: undefined,
  warning: undefined,
  error: undefined,
  className: undefined,
  validationFirst: false
};
FormGroup.displayName = 'FormGroup';
var propTypes = Object.assign({}, FormGroup.propTypes);
delete propTypes.children;

var getChecked = function getChecked(_ref) {
  var optionGroupValue = _ref.optionGroupValue,
      optionValue = _ref.optionValue,
      optionChecked = _ref.optionChecked;
  var checked;
  if (optionGroupValue !== undefined) checked = optionValue === optionGroupValue;
  if (optionChecked !== undefined) checked = optionChecked;
  return checked;
};

var FormOptionFieldInput = function FormOptionFieldInput(_ref2) {
  var option = _ref2.option,
      optionGroupValue = _ref2.optionGroupValue,
      props = _objectWithoutPropertiesLoose(_ref2, ["option", "optionGroupValue"]);

  return /*#__PURE__*/React.createElement(FormOption, _extends({}, props, option, {
    checked: getChecked({
      optionGroupValue: optionGroupValue,
      optionValue: option.value,
      optionChecked: option.checked
    })
  }));
};

FormOptionFieldInput.propTypes = {
  option: PropTypes.shape({
    label: PropTypes.node,
    value: PropTypes.string,
    checked: PropTypes.bool
  }).isRequired,
  optionGroupValue: PropTypes.string
};
FormOptionFieldInput.defaultProps = {
  optionGroupValue: undefined
};
FormOptionFieldInput.displayName = 'FormOptionFieldInput';

var FormOptionField = function FormOptionField(_ref3) {
  var className = _ref3.className,
      element = _ref3.element,
      name = _ref3.name,
      required = _ref3.required,
      requiredLabel = _ref3.requiredLabel,
      optional = _ref3.optional,
      validationFirst = _ref3.validationFirst,
      label = _ref3.label,
      labelElement = _ref3.labelElement,
      hideLabel = _ref3.hideLabel,
      error = _ref3.error,
      warning = _ref3.warning,
      info = _ref3.info,
      options = _ref3.options,
      optionType = _ref3.optionType,
      listType = _ref3.listType,
      outlined = _ref3.outlined,
      props = _objectWithoutPropertiesLoose(_ref3, ["className", "element", "name", "required", "requiredLabel", "optional", "validationFirst", "label", "labelElement", "hideLabel", "error", "warning", "info", "options", "optionType", "listType", "outlined"]);

  var optionGroupValue = // eslint-disable-next-line react/destructuring-assignment
  props.value !== undefined ? props.value.toString() : undefined;
  var optionInputProps = Object.assign({}, props, {
    type: optionType
  });
  return /*#__PURE__*/React.createElement(FormGroup, {
    className: className,
    element: element,
    error: error,
    hideLabel: hideLabel,
    info: info,
    label: label,
    labelElement: labelElement,
    name: name,
    optional: optional,
    required: required,
    validationFirst: validationFirst,
    warning: warning
  }, function (inputProps) {
    return options.length > 1 ? /*#__PURE__*/React.createElement(List, {
      type: listType,
      spaced: outlined && 'sm'
    }, options.map(function (option) {
      return /*#__PURE__*/React.createElement(ListItem, {
        type: listType,
        key: name + "-" + option.value
      }, /*#__PURE__*/React.createElement(FormOptionFieldInput, _extends({}, inputProps, optionInputProps, {
        option: option,
        optionGroupValue: optionGroupValue,
        innerRef: option.innerRef,
        outlined: outlined
      })));
    })) : /*#__PURE__*/React.createElement(FormOptionFieldInput, _extends({}, inputProps, optionInputProps, {
      option: options[0],
      optionGroupValue: optionGroupValue,
      innerRef: options[0].innerRef,
      outlined: outlined
    }));
  });
};

FormOptionField.propTypes = Object.assign({}, propTypes, {
  /** Allows the top-level element to be customized */
  element: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),

  /** The list of options to be made available */
  options: PropTypes.arrayOf(PropTypes.object).isRequired,

  /** What format the options should take */
  optionType: PropTypes.oneOf(['radio', 'checkbox', 'switch']),
  labelElement: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),

  /** How the group of options should be displayed, e.g. inline */
  listType: PropTypes.string,

  /** Whether the option should have a border */
  outlined: PropTypes.bool
});
FormOptionField.defaultProps = {
  element: 'fieldset',
  optionType: 'radio',
  labelElement: 'legend',
  listType: 'bare',
  outlined: false
};
FormOptionField.displayName = 'FormOptionField';
/**
 * @see Combines [`FormOptionField`](#/Components/Form?id=formoptionfield) and [`FormGroup`](#/Components/Form?id=formgroup-1) for a more feature rich component.
 */

var CheckboxField = function CheckboxField(props) {
  return /*#__PURE__*/React.createElement(FormOptionField, _extends({
    optionType: "checkbox"
  }, props));
};

CheckboxField.displayName = 'CheckboxField';

var Fieldset = function Fieldset(_ref) {
  var children = _ref.children,
      className = _ref.className,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "className"]);

  return /*#__PURE__*/React.createElement(Card, _extends({}, rest, {
    className: className
  }), /*#__PURE__*/React.createElement("fieldset", null, children));
};

Fieldset.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};
Fieldset.defaultProps = {
  children: undefined,
  className: undefined
};
Fieldset.displayName = 'Fieldset';

var Form = function Form(_ref) {
  var children = _ref.children,
      className = _ref.className,
      onSubmit = _ref.onSubmit,
      useBrowserValidation = _ref.useBrowserValidation,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "className", "onSubmit", "useBrowserValidation"]);

  return /*#__PURE__*/React.createElement("form", _extends({}, rest, {
    noValidate: !useBrowserValidation,
    onSubmit: onSubmit,
    className: className
  }), children);
};

Form.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  onSubmit: PropTypes.func,
  useBrowserValidation: PropTypes.bool
};
Form.defaultProps = {
  className: undefined,
  onSubmit: undefined,
  useBrowserValidation: false
};
Form.displayName = 'Form';

var InputAction = function InputAction(_ref) {
  var children = _ref.children;
  var element = React.Children.only(children);
  return React.cloneElement(element, {
    className: classnames('ln-c-input-group__action', element.props && element.props.className)
  });
};

InputAction.propTypes = {
  children: PropTypes.node
};
InputAction.defaultProps = {
  children: undefined
};
InputAction.displayName = 'InputAction';

var InputButton = function InputButton(_ref) {
  var children = _ref.children;
  var element = React.Children.only(children);
  return React.cloneElement(element, {
    className: classnames('ln-c-input-group__button', element.props && element.props.className)
  });
};

InputButton.propTypes = {
  children: PropTypes.node
};
InputButton.defaultProps = {
  children: undefined
};
InputButton.displayName = 'InputButton';

var InputControl = function InputControl(_ref) {
  var children = _ref.children;
  var element = React.Children.only(children);
  return React.cloneElement(element, {
    className: classnames('ln-c-input-group__control', element.props && element.props.className)
  });
};

InputControl.propTypes = {
  children: PropTypes.node
};
InputControl.defaultProps = {
  children: undefined
};
InputControl.displayName = 'InputControl';

var InputGroup = function InputGroup(_ref) {
  var element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      hasIcon = _ref.hasIcon,
      hasAction = _ref.hasAction,
      hasButton = _ref.hasButton,
      disabled = _ref.disabled,
      rest = _objectWithoutPropertiesLoose(_ref, ["element", "children", "className", "hasIcon", "hasAction", "hasButton", "disabled"]);

  var Element = element;
  var hasIconClass = hasIcon;
  var hasActionClass = hasAction;
  var hasButtonClass = hasButton;
  React.Children.forEach(children, function (child) {
    if (React.isValidElement(child)) {
      var componentName = getComponentName(child);

      if (componentName === 'InputIcon') {
        hasIconClass = true;
      }

      if (componentName === 'InputAction') {
        hasActionClass = true;
      }

      if (componentName === 'InputButton') {
        hasButtonClass = true;
      }
    }
  });
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    className: classnames('ln-c-input-group', hasIconClass && 'ln-c-input-group--has-icon', hasActionClass && 'ln-c-input-group--has-action', hasButtonClass && 'ln-c-input-group--has-button', disabled && 'is-disabled', className)
  }), children);
};

InputGroup.propTypes = {
  element: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),
  children: PropTypes.node,
  className: PropTypes.string,
  hasIcon: PropTypes.bool,
  hasAction: PropTypes.bool,
  hasButton: PropTypes.bool,

  /** Applies disabled styling to elements within the group  */
  disabled: PropTypes.bool
};
InputGroup.defaultProps = {
  element: 'div',
  children: undefined,
  className: undefined,
  hasIcon: false,
  hasAction: false,
  hasButton: false,
  disabled: false
};
InputGroup.displayName = 'InputGroup';

var InputIcon = function InputIcon(_ref) {
  var children = _ref.children;
  var element = React.Children.only(children);
  return React.cloneElement(element, {
    className: classnames('ln-c-input-group__icon', element.props && element.props.className),
    'aria-hidden': true
  });
};

InputIcon.propTypes = {
  children: PropTypes.node
};
InputIcon.defaultProps = {
  children: undefined
};
InputIcon.displayName = 'InputIcon';

var Legend = function Legend(_ref) {
  var children = _ref.children,
      headingLevel = _ref.headingLevel,
      rest = _objectWithoutPropertiesLoose(_ref, ["children", "headingLevel"]);

  return /*#__PURE__*/React.createElement(Heading, _extends({}, rest, {
    element: "legend",
    level: headingLevel
  }), children);
};

Legend.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  fontStyle: PropTypes.string,
  headingLevel: PropTypes.number
};
Legend.defaultProps = {
  children: undefined,
  className: undefined,
  fontStyle: undefined,
  headingLevel: 3
};
Legend.displayName = 'Legend';

var TextInput = function TextInput(_ref) {
  var className = _ref.className,
      props = _objectWithoutPropertiesLoose(_ref, ["className"]);

  return /*#__PURE__*/React.createElement(Input, _extends({
    className: classnames('ln-c-text-input', className)
  }, props));
};

TextInput.propTypes = {
  className: PropTypes.string
};
TextInput.defaultProps = {
  className: undefined
};
TextInput.displayName = 'TextInput';

var NumberInput = /*#__PURE__*/function (_Component) {
  _inheritsLoose(NumberInput, _Component);

  function NumberInput(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    var defaultValue = props.defaultValue;
    _this.handleChange = _this.handleChange.bind(_assertThisInitialized(_this));
    _this.state = {
      value: defaultValue
    };
    return _this;
  }

  var _proto = NumberInput.prototype;

  _proto.handleChange = function handleChange(e) {
    var _this$props = this.props,
        inputFilter = _this$props.inputFilter,
        onChange = _this$props.onChange;
    var value = e.target.value.replace(inputFilter, '');
    this.setState({
      value: value
    });

    if (onChange) {
      onChange(e, value);
    }
  };

  _proto.render = function render() {
    var value = this.state.value;

    var _this$props2 = this.props,
        increment = _this$props2.increment,
        pattern = _this$props2.pattern,
        rest = _objectWithoutPropertiesLoose(_this$props2, ["increment", "pattern"]);

    return /*#__PURE__*/React.createElement(TextInput, _extends({}, filterProps(rest, ['inputFilter', 'onChange', 'defaultValue']), {
      value: value,
      onChange: this.handleChange,
      type: increment ? 'number' : undefined,
      inputMode: "decimal",
      pattern: pattern
    }));
  };

  return NumberInput;
}(Component);

NumberInput.propTypes = {
  /** Enables use of methods to filter out specific inputs */
  onChange: PropTypes.func,

  /** Add browsers stepper control */
  increment: PropTypes.bool,

  /** Override filter used to restrict non numeric characters */
  inputFilter: PropTypes.instanceOf(RegExp),

  /** Browser validation regex, defaulted to trigger number keyboard in iOS */
  pattern: PropTypes.string,
  defaultValue: PropTypes.string
};
NumberInput.defaultProps = {
  increment: false,
  onChange: undefined,
  inputFilter: /[^0-9.,]/g,
  pattern: '[0-9]*',
  defaultValue: ''
};
NumberInput.displayName = 'NumberInput';
/**
 * @see Combines [`NumberInput`](#/Components/Inputs?id=textinput) and [`FormGroup`](#/Components/Form?id=formgroup-1) for a more feature rich component.
 * Also builds in functionality offered by the `InputGroup`(#/Components?id=inputgroup-1) components
 */

var NumberInputField = function NumberInputField(_ref) {
  var action = _ref.action,
      actionFirst = _ref.actionFirst,
      button = _ref.button,
      className = _ref.className,
      element = _ref.element,
      error = _ref.error,
      hideLabel = _ref.hideLabel,
      icon = _ref.icon,
      info = _ref.info,
      label = _ref.label,
      labelElement = _ref.labelElement,
      name = _ref.name,
      optional = _ref.optional,
      required = _ref.required,
      validationFirst = _ref.validationFirst,
      warning = _ref.warning,
      props = _objectWithoutPropertiesLoose(_ref, ["action", "actionFirst", "button", "className", "element", "error", "hideLabel", "icon", "info", "label", "labelElement", "name", "optional", "required", "validationFirst", "warning"]);

  return /*#__PURE__*/React.createElement(FormGroup, {
    className: className,
    element: element,
    error: error,
    hideLabel: hideLabel,
    info: info,
    label: label,
    labelElement: labelElement,
    name: name,
    optional: optional,
    required: required,
    validationFirst: validationFirst,
    warning: warning
  }, function (inputProps) {
    return /*#__PURE__*/React.createElement(InputGroup, null, icon && /*#__PURE__*/React.createElement(InputIcon, null, icon), action && actionFirst && /*#__PURE__*/React.createElement(InputAction, null, action), /*#__PURE__*/React.createElement(InputControl, null, /*#__PURE__*/React.createElement(NumberInput, _extends({}, inputProps, filterProps(props, ['setValue', 'clearValue', 'setRef', 'hasAction', 'onClear'])))), action && !actionFirst && /*#__PURE__*/React.createElement(InputAction, null, action), button && /*#__PURE__*/React.createElement(InputButton, null, button));
  });
};

NumberInputField.propTypes = Object.assign({}, propTypes, {
  icon: PropTypes.node,
  action: PropTypes.node,
  button: PropTypes.node,
  actionFirst: PropTypes.bool
});
NumberInputField.defaultProps = {
  icon: undefined,
  action: undefined,
  button: undefined,
  actionFirst: false
};
NumberInputField.displayName = 'NumberInputField';

var Password = function Password(_ref) {
  var hasToggle = _ref.hasToggle,
      props = _objectWithoutPropertiesLoose(_ref, ["hasToggle"]);

  var _useState = useState(false),
      showText = _useState[0],
      setTextVisibility = _useState[1];

  return /*#__PURE__*/React.createElement(InputGroup, null, /*#__PURE__*/React.createElement(InputControl, null, /*#__PURE__*/React.createElement(TextInput, _extends({}, props, {
    type: showText ? 'text' : 'password'
  }))), hasToggle && /*#__PURE__*/React.createElement(InputAction, null, /*#__PURE__*/React.createElement(Button, {
    onClick: function onClick() {
      return setTextVisibility(!showText);
    }
  }, showText ? 'Hide' : 'Show', /*#__PURE__*/React.createElement("span", {
    className: "ln-u-visually-hidden"
  }, " password"))));
};

Password.propTypes = {
  hasToggle: PropTypes.bool
};
Password.defaultProps = {
  hasToggle: false
};
Password.displayName = 'Password'; // Named export preserved for backwards compatibility

var DEFAULT_LEVELS = {
  0: {
    className: '',
    caption: 'Too short',
    test: function test() {
      return true;
    }
  },
  1: {
    className: 'is-level-1',
    caption: 'Bad',
    test: function test(value) {
      return value.length > 7;
    }
  },
  2: {
    className: 'is-level-2',
    caption: 'Ok',
    test: function test(value) {
      return value.length > 9;
    }
  },
  3: {
    className: 'is-level-3',
    caption: 'Good',
    test: function test(value) {
      return value.length > 11;
    }
  },
  4: {
    className: 'is-level-4',
    caption: 'Great',
    test: function test(value) {
      return value.length > 13;
    }
  }
};

var getLevel = function getLevel(value, levelMap) {
  var levels = Object.keys(levelMap);
  var reversedLevels = levels.reverse();

  if (value !== undefined) {
    for (var i = 0; i < levels.length; i += 1) {
      if (levelMap[reversedLevels[i]].test(value)) {
        return levelMap[reversedLevels[i]];
      }
    }
  }

  return levelMap[levels[0]];
};
/**
 * @see Combines [`Password`](#/Components/Inputs?id=password) and [`FormGroup`](#/Components/Form?id=formgroup-1) for a more feature rich component.
 */


var PasswordField = /*#__PURE__*/function (_Component) {
  _inheritsLoose(PasswordField, _Component);

  function PasswordField(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.state = {
      value: props.defaultValue || ''
    };
    _this.handleChange = _this.handleChange.bind(_assertThisInitialized(_this));
    return _this;
  }

  var _proto = PasswordField.prototype;

  _proto.handleChange = function handleChange(event) {
    var onChange = this.props.onChange;
    this.setState({
      value: event.target.value
    });

    if (onChange) {
      onChange(event);
    }
  };

  _proto.render = function render() {
    var _this2 = this;

    var value = this.state.value;

    var _this$props = this.props,
        className = _this$props.className,
        error = _this$props.error,
        hideLabel = _this$props.hideLabel,
        info = _this$props.info,
        label = _this$props.label,
        labelElement = _this$props.labelElement,
        levels = _this$props.levels,
        name = _this$props.name,
        optional = _this$props.optional,
        required = _this$props.required,
        strengthIndicator = _this$props.strengthIndicator,
        validationFirst = _this$props.validationFirst,
        warning = _this$props.warning,
        props = _objectWithoutPropertiesLoose(_this$props, ["className", "error", "hideLabel", "info", "label", "labelElement", "levels", "name", "optional", "required", "strengthIndicator", "validationFirst", "warning"]);

    var strengthIndicatorId = name + "StrengthIndicator";
    var level = getLevel(value, levels);
    return /*#__PURE__*/React.createElement(FormGroup, {
      className: className,
      error: error,
      hideLabel: hideLabel,
      info: info,
      label: label,
      labelElement: labelElement,
      name: name,
      optional: optional,
      required: required,
      validationFirst: validationFirst,
      warning: warning
    }, function (_ref) {
      var describedBy = _ref['aria-describedby'],
          inputProps = _objectWithoutPropertiesLoose(_ref, ["aria-describedby"]);

      return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Password, _extends({}, inputProps, {
        "aria-describedby": describedBy ? describedBy + " " + strengthIndicatorId + " " : strengthIndicatorId
      }, props, {
        value: value,
        onChange: _this2.handleChange
      })), strengthIndicator && /*#__PURE__*/React.createElement("div", {
        className: "ln-c-password-strength-meter " + level.className
      }, /*#__PURE__*/React.createElement("p", {
        id: strengthIndicatorId,
        className: "ln-c-password-strength-meter__label",
        "aria-live": "polite",
        "aria-atomic": "true"
      }, "Strength:", ' ', /*#__PURE__*/React.createElement("span", {
        className: "ln-c-password-strength-meter__value"
      }, level.caption))));
    });
  };

  return PasswordField;
}(Component);

PasswordField.getDerivedStateFromProps = function (props, state) {
  return props.value !== undefined && props.value !== state.value ? {
    value: props.value
  } : null;
};

PasswordField.propTypes = Object.assign({}, propTypes, {
  levels: PropTypes.shape({}),
  strengthIndicator: PropTypes.bool
});
PasswordField.defaultProps = {
  levels: DEFAULT_LEVELS,
  strengthIndicator: false
};
PasswordField.displayName = 'PasswordField';

var RadioButton = function RadioButton(props) {
  return /*#__PURE__*/React.createElement(FormOption, _extends({
    type: "radio"
  }, props));
};

RadioButton.displayName = 'RadioButton';
/**
 * @component
 * @see Combines [`FormOptionField`](#/Components/Form?id=formoptionfield) and [`FormGroup`](#/Components/Form?id=formgroup-1).
 */

var RadioButtonField = function RadioButtonField(props) {
  return /*#__PURE__*/React.createElement(FormOptionField, _extends({
    optionType: "radio"
  }, props));
};

RadioButtonField.displayName = 'RadioButtonField';

var SearchInput = function SearchInput(_ref) {
  var className = _ref.className,
      icon = _ref.icon,
      action = _ref.action,
      button = _ref.button,
      hasIcon = _ref.hasIcon,
      hasAction = _ref.hasAction,
      hasButton = _ref.hasButton,
      props = _objectWithoutPropertiesLoose(_ref, ["className", "icon", "action", "button", "hasIcon", "hasAction", "hasButton"]);

  return /*#__PURE__*/React.createElement(InputGroup, {
    className: className
  }, hasIcon && /*#__PURE__*/React.createElement(InputIcon, null, icon), /*#__PURE__*/React.createElement(InputControl, null, /*#__PURE__*/React.createElement(TextInput, _extends({
    type: "search"
  }, props))), hasAction && /*#__PURE__*/React.createElement(InputAction, null, action), hasButton && /*#__PURE__*/React.createElement(InputButton, null, button));
};

SearchInput.propTypes = {
  className: PropTypes.string,
  icon: PropTypes.node,
  action: PropTypes.node,
  button: PropTypes.node,
  hasIcon: PropTypes.bool,
  hasAction: PropTypes.bool,
  hasButton: PropTypes.bool
};
SearchInput.defaultProps = {
  className: undefined,
  icon: /*#__PURE__*/React.createElement(Search, null),
  button: undefined,
  action: /*#__PURE__*/React.createElement(SearchButton, {
    inputAction: true
  }),
  hasIcon: false,
  hasAction: false,
  hasButton: false
};
SearchInput.displayName = 'SearchInput';
var excludeProps = ['setValue', 'clearValue', 'setRef', 'onClear'];
/**
 * @see Combines [`SearchInput`](#/Components/Inputs?id=searchinput) and [`FormGroup`](#/Components/Form?id=formgroup-1) for a more feature rich component.
 */

var SearchField = function SearchField(_ref) {
  var button = _ref.button,
      className = _ref.className,
      element = _ref.element,
      error = _ref.error,
      hideLabel = _ref.hideLabel,
      info = _ref.info,
      label = _ref.label,
      labelElement = _ref.labelElement,
      name = _ref.name,
      optional = _ref.optional,
      required = _ref.required,
      validationFirst = _ref.validationFirst,
      warning = _ref.warning,
      props = _objectWithoutPropertiesLoose(_ref, ["button", "className", "element", "error", "hideLabel", "info", "label", "labelElement", "name", "optional", "required", "validationFirst", "warning"]);

  return /*#__PURE__*/React.createElement(FormGroup, {
    className: className,
    element: element,
    error: error,
    hideLabel: hideLabel,
    info: info,
    label: label,
    labelElement: labelElement,
    name: name,
    optional: optional,
    required: required,
    validationFirst: validationFirst,
    warning: warning
  }, function (inputProps) {
    return /*#__PURE__*/React.createElement(SearchInput, _extends({}, inputProps, filterProps(props, excludeProps), {
      button: button
    }));
  });
};

SearchField.propTypes = Object.assign({}, propTypes, {
  button: PropTypes.node
});
SearchField.defaultProps = {
  button: /*#__PURE__*/React.createElement(SearchButton, null)
};
SearchField.displayName = 'SearchField';
var DEFAULT_PLACEHOLDER = 'Please select';

var Select = function Select(_ref) {
  var name = _ref.name,
      options = _ref.options,
      required = _ref.required,
      disabled = _ref.disabled,
      onBlur = _ref.onBlur,
      onChange = _ref.onChange,
      onFocus = _ref.onFocus,
      value = _ref.value,
      className = _ref.className,
      placeholder = _ref.placeholder,
      defaultValue = _ref.defaultValue,
      readOnly = _ref.readOnly,
      tabIndex = _ref.tabIndex,
      invalid = _ref.invalid,
      innerRef = _ref.innerRef,
      rest = _objectWithoutPropertiesLoose(_ref, ["name", "options", "required", "disabled", "onBlur", "onChange", "onFocus", "value", "className", "placeholder", "defaultValue", "readOnly", "tabIndex", "invalid", "innerRef"]);

  return /*#__PURE__*/React.createElement("select", _extends({}, rest, {
    id: name,
    name: name,
    className: classnames('ln-c-select', className),
    onBlur: onBlur,
    onChange: onChange,
    onFocus: onFocus,
    value: value,
    defaultValue: defaultValue,
    required: required,
    tabIndex: tabIndex,
    disabled: disabled,
    readOnly: readOnly,
    ref: innerRef,
    "aria-disabled": disabled || undefined,
    "aria-required": required === false ? required : undefined,
    "aria-invalid": invalid || undefined
  }), placeholder && /*#__PURE__*/React.createElement("option", {
    value: ""
  }, typeof placeholder === 'string' ? placeholder : DEFAULT_PLACEHOLDER), options.map(function (option) {
    var label = option.label,
        optionValue = option.value,
        optionDisabled = option.disabled,
        optionRest = _objectWithoutPropertiesLoose(option, ["label", "value", "disabled"]);

    return /*#__PURE__*/React.createElement("option", _extends({}, optionRest, {
      key: name + "-" + option.value,
      value: optionValue,
      disabled: !!optionDisabled,
      "aria-disabled": !!optionDisabled || undefined
    }), label);
  }));
};

Select.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  disabled: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  value: PropTypes.string,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  defaultValue: PropTypes.string,
  className: PropTypes.string,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  tabIndex: PropTypes.number,
  invalid: PropTypes.bool,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
};
Select.defaultProps = {
  disabled: false,
  onBlur: undefined,
  onChange: undefined,
  onFocus: undefined,
  value: undefined,
  defaultValue: undefined,
  placeholder: DEFAULT_PLACEHOLDER,
  className: undefined,
  required: undefined,
  readOnly: false,
  tabIndex: undefined,
  invalid: undefined,
  innerRef: undefined
};
Select.displayName = 'Select';

var SelectField = function SelectField(_ref) {
  var className = _ref.className,
      error = _ref.error,
      hideLabel = _ref.hideLabel,
      info = _ref.info,
      label = _ref.label,
      labelElement = _ref.labelElement,
      name = _ref.name,
      optional = _ref.optional,
      required = _ref.required,
      validationFirst = _ref.validationFirst,
      warning = _ref.warning,
      props = _objectWithoutPropertiesLoose(_ref, ["className", "error", "hideLabel", "info", "label", "labelElement", "name", "optional", "required", "validationFirst", "warning"]);

  return /*#__PURE__*/React.createElement(FormGroup, {
    className: className,
    error: error,
    hideLabel: hideLabel,
    info: info,
    label: label,
    labelElement: labelElement,
    name: name,
    optional: optional,
    required: required,
    validationFirst: validationFirst,
    warning: warning
  }, function (inputProps) {
    return /*#__PURE__*/React.createElement(Select, _extends({}, inputProps, props));
  });
};

SelectField.propTypes = propTypes;
SelectField.displayName = 'SelectField';

var Switch = function Switch(props) {
  return /*#__PURE__*/React.createElement(FormOption, _extends({
    type: "switch",
    role: "switch"
  }, props));
};

Switch.displayName = 'Switch';
/**
 * @see Combines [`FormOptionField`](#/Components/Form?id=formoptionfield) and [`FormGroup`](#/Components/Form?id=formgroup-1) for a more feature rich component.
 */

var SwitchField = function SwitchField(props) {
  return /*#__PURE__*/React.createElement(FormOptionField, _extends({
    optionType: "switch"
  }, props));
};

SwitchField.displayName = 'SwitchField';

var TextArea = function TextArea(_ref) {
  var id = _ref.id,
      name = _ref.name,
      placeholder = _ref.placeholder,
      disabled = _ref.disabled,
      onBlur = _ref.onBlur,
      onChange = _ref.onChange,
      onFocus = _ref.onFocus,
      value = _ref.value,
      defaultValue = _ref.defaultValue,
      minLength = _ref.minLength,
      maxLength = _ref.maxLength,
      autoComplete = _ref.autoComplete,
      autoCapitalize = _ref.autoCapitalize,
      required = _ref.required,
      readOnly = _ref.readOnly,
      spellCheck = _ref.spellCheck,
      tabIndex = _ref.tabIndex,
      className = _ref.className,
      invalid = _ref.invalid,
      cols = _ref.cols,
      rows = _ref.rows,
      wrap = _ref.wrap,
      innerRef = _ref.innerRef,
      rest = _objectWithoutPropertiesLoose(_ref, ["id", "name", "placeholder", "disabled", "onBlur", "onChange", "onFocus", "value", "defaultValue", "minLength", "maxLength", "autoComplete", "autoCapitalize", "required", "readOnly", "spellCheck", "tabIndex", "className", "invalid", "cols", "rows", "wrap", "innerRef"]);

  return /*#__PURE__*/React.createElement("textarea", _extends({}, rest, {
    id: id || name,
    name: name,
    className: classnames('ln-c-text-input ln-c-text-input--textarea', className),
    onBlur: onBlur,
    onChange: onChange,
    onFocus: onFocus,
    value: value,
    defaultValue: defaultValue,
    placeholder: placeholder,
    tabIndex: tabIndex,
    autoComplete: autoComplete,
    autoCapitalize: autoCapitalize,
    spellCheck: spellCheck,
    required: required,
    minLength: minLength,
    maxLength: maxLength,
    readOnly: readOnly,
    disabled: disabled,
    "aria-disabled": disabled || undefined,
    "aria-required": required === false ? required : undefined,
    "aria-invalid": invalid || undefined,
    cols: cols,
    rows: rows,
    wrap: wrap,
    ref: innerRef
  }));
};

TextArea.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  autoComplete: PropTypes.string,
  autoCapitalize: PropTypes.string,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  spellCheck: PropTypes.oneOf(['default', true, false]),
  tabIndex: PropTypes.number,
  className: PropTypes.string,
  invalid: PropTypes.bool,
  rows: PropTypes.number,
  cols: PropTypes.number,
  wrap: PropTypes.string,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
};
TextArea.defaultProps = {
  id: undefined,
  disabled: false,
  placeholder: undefined,
  onBlur: undefined,
  onChange: undefined,
  onFocus: undefined,
  value: undefined,
  defaultValue: undefined,
  checked: undefined,
  defaultChecked: undefined,
  minLength: undefined,
  maxLength: undefined,
  autoComplete: undefined,
  autoCapitalize: undefined,
  required: undefined,
  readOnly: false,
  spellCheck: undefined,
  tabIndex: undefined,
  className: undefined,
  invalid: undefined,
  rows: undefined,
  cols: undefined,
  wrap: undefined,
  innerRef: undefined
};
TextArea.displayName = 'TextArea';
/**
 * @see Combines [`TextArea`](#/Components/Inputs?id=textarea) and [`FormGroup`](#/Components/Form?id=formgroup-1)
 * for a more feature rich component. See their component demos for extended functionality.
 */

var TextAreaField = /*#__PURE__*/function (_Component) {
  _inheritsLoose(TextAreaField, _Component);

  function TextAreaField(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.state = {
      value: props.defaultValue || ''
    };
    _this.handleChange = _this.handleChange.bind(_assertThisInitialized(_this));
    return _this;
  }

  var _proto = TextAreaField.prototype;

  _proto.handleChange = function handleChange(event) {
    var onChange = this.props.onChange;
    this.setState({
      value: event.target.value
    });

    if (onChange) {
      onChange(event);
    }
  };

  _proto.render = function render() {
    var _this2 = this;

    var value = this.state.value;

    var _this$props = this.props,
        characterCount = _this$props.characterCount,
        className = _this$props.className,
        error = _this$props.error,
        hideLabel = _this$props.hideLabel,
        info = _this$props.info,
        label = _this$props.label,
        labelElement = _this$props.labelElement,
        name = _this$props.name,
        optional = _this$props.optional,
        required = _this$props.required,
        validationFirst = _this$props.validationFirst,
        warning = _this$props.warning,
        props = _objectWithoutPropertiesLoose(_this$props, ["characterCount", "className", "error", "hideLabel", "info", "label", "labelElement", "name", "optional", "required", "validationFirst", "warning"]);

    var counterId = name + "Counter";
    return /*#__PURE__*/React.createElement(FormGroup, {
      className: className,
      error: error,
      hideLabel: hideLabel,
      info: info,
      label: label,
      labelElement: labelElement,
      name: name,
      optional: optional,
      required: required,
      validationFirst: validationFirst,
      warning: warning
    }, function (_ref) {
      var describedBy = _ref['aria-describedby'],
          inputProps = _objectWithoutPropertiesLoose(_ref, ["aria-describedby"]);

      return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(TextArea, _extends({}, inputProps, {
        "aria-describedby": describedBy || characterCount ? (describedBy || '') + " " + (characterCount ? counterId : '') : undefined
      }, props, {
        onChange: _this2.handleChange,
        value: value
      })), characterCount && props.maxLength && /*#__PURE__*/React.createElement("p", {
        id: counterId,
        className: "ln-u-margin-ends",
        "aria-live": "polite",
        "aria-atomic": "true"
      }, value.length, /*#__PURE__*/React.createElement("span", {
        className: "ln-u-visually-hidden"
      }, " out of "), /*#__PURE__*/React.createElement("span", {
        "aria-hidden": "true"
      }, " / "), props.maxLength));
    });
  };

  return TextAreaField;
}(Component);

TextAreaField.getDerivedStateFromProps = function (props, state) {
  return props.value !== undefined && props.value !== state.value ? {
    value: props.value
  } : null;
};

TextAreaField.propTypes = Object.assign({}, propTypes, {
  characterCount: PropTypes.bool
});
TextAreaField.defaultProps = {
  characterCount: false
};
TextAreaField.displayName = 'TextAreaField';
/**
 * @see Combines [`TextInput`](#/Components/Inputs?id=textinput) and [`FormGroup`](#/Components/Form?id=formgroup-1) for a more feature rich component.
 * Also builds in functionality offered by the `InputGroup`(#/Components?id=inputgroup-1) components
 */

var TextInputField = function TextInputField(_ref) {
  var action = _ref.action,
      actionFirst = _ref.actionFirst,
      button = _ref.button,
      className = _ref.className,
      element = _ref.element,
      error = _ref.error,
      hideLabel = _ref.hideLabel,
      icon = _ref.icon,
      info = _ref.info,
      label = _ref.label,
      labelElement = _ref.labelElement,
      name = _ref.name,
      optional = _ref.optional,
      required = _ref.required,
      validationFirst = _ref.validationFirst,
      warning = _ref.warning,
      props = _objectWithoutPropertiesLoose(_ref, ["action", "actionFirst", "button", "className", "element", "error", "hideLabel", "icon", "info", "label", "labelElement", "name", "optional", "required", "validationFirst", "warning"]);

  return /*#__PURE__*/React.createElement(FormGroup, {
    className: className,
    element: element,
    error: error,
    hideLabel: hideLabel,
    info: info,
    label: label,
    labelElement: labelElement,
    name: name,
    optional: optional,
    required: required,
    validationFirst: validationFirst,
    warning: warning
  }, function (inputProps) {
    return /*#__PURE__*/React.createElement(InputGroup, null, icon && /*#__PURE__*/React.createElement(InputIcon, null, icon), action && actionFirst && /*#__PURE__*/React.createElement(InputAction, null, action), /*#__PURE__*/React.createElement(InputControl, null, /*#__PURE__*/React.createElement(TextInput, _extends({}, inputProps, filterProps(props, ['setValue', 'clearValue', 'setRef', 'hasAction', 'onClear'])))), action && !actionFirst && /*#__PURE__*/React.createElement(InputAction, null, action), button && /*#__PURE__*/React.createElement(InputButton, null, button));
  });
};

TextInputField.propTypes = Object.assign({}, propTypes, {
  icon: PropTypes.node,
  action: PropTypes.node,
  button: PropTypes.node,
  actionFirst: PropTypes.bool
});
TextInputField.defaultProps = {
  icon: undefined,
  action: undefined,
  button: undefined,
  actionFirst: false
};
TextInputField.displayName = 'TextInputField';

var onChangeHandler = function onChangeHandler(_ref) {
  var onChange = _ref.onChange,
      setValue = _ref.setValue;
  return function (e) {
    if (onChange) {
      onChange(e);
    }

    setValue(e.target.value);
  };
};

var getClearActionProps = function getClearActionProps(value, clearValue) {
  return {
    hasAction: true,
    action: /*#__PURE__*/React.createElement(CancelButton, {
      hidden: !value || value.length === 0,
      onClick: clearValue,
      "aria-label": "Clear the field"
    })
  };
};

var setFieldGroupElement = function setFieldGroupElement(_ref2) {
  var setRef = _ref2.setRef;
  return {
    // eslint-disable-next-line react/prop-types, react/display-name
    element: function element(_ref3) {
      var children = _ref3.children,
          className = _ref3.className;
      return /*#__PURE__*/React.createElement("div", {
        ref: setRef,
        className: className
      }, children);
    }
  };
};

var withClear = function withClear(WrappedField) {
  var input;

  var setRef = function setRef(field) {
    if (field) input = field.querySelector('input');
  };

  var _setFieldGroupElement = setFieldGroupElement({
    setRef: setRef
  }),
      element = _setFieldGroupElement.element;

  var Composed = function Composed(props) {
    var _useState = useState(props.defaultValue || props.value || ''),
        value = _useState[0],
        setValue = _useState[1];

    var onChange = onChangeHandler({
      onChange: props.onChange,
      setValue: setValue
    });

    var clearValue = function clearValue() {
      setValue('');
      if (input) input.focus();
      if (props.onClear) props.onClear();
    };

    return /*#__PURE__*/React.createElement(WrappedField, _extends({
      onChange: onChange,
      value: value,
      element: element,
      clearValue: clearValue
    }, getClearActionProps(value, clearValue), props));
  };

  Composed.displayName = 'WithClear';
  return Composed;
};

export { Checkbox, CheckboxField, FieldInfo, Fieldset, Form, FormGroup, FormOption, Input, InputAction, InputButton, InputControl, InputGroup, InputIcon, Label, LabelInfo, Legend, NumberInput, NumberInputField, Password, Password as PasswordComponent, PasswordField, RadioButton, RadioButtonField, SearchField, SearchInput, Select, SelectField, Switch, SwitchField, TextArea, TextAreaField, TextInput, TextInputField, withClear };